import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Package, Star, Truck, Trash2, Folder, Upload, Wand2, Sparkles, Edit, FileText } from 'lucide-react';
import type { Product } from '../../types/product';
import { DeleteConfirmationModal } from '../common/DeleteConfirmationModal';
import { DescriptionTemplateSelector } from '../templates/description/DescriptionTemplateSelector';
import { ImagePopup } from '../folders/ImagePopup';
import { PriceDisplay } from '../prices/PriceDisplay';
import { EditableTitle } from './title/EditableTitle';
import { MemoInput } from '../MemoInput';
import { DragDropImageUpload } from './imageHandle/DragDropImageUpload';
import { createDisplayTitle } from '../../utils/memoUtils';
import { generateTitleFromImages, generateTitleAndCategoryFromImages } from '../../utils/openai';
import { calculateCharacterCount } from '../../utils/characterCount';
import { useAiCredits } from '../../hooks/useAiCredits';
import { getItemNumberDisplay } from '../../utils/titleProcessor';
import { useAuthStore } from '../../stores/authStore';
import { InsufficientCreditsModal } from '../notifications/InsufficientCreditsModal';
import { useDescriptionTemplateStore } from '../../stores/descriptionTemplateStore';
import { useAiCreditsStore } from '../../stores/aiCreditsStore';
import {
  AI_TITLE_GENERATION_CREDIT_COST,
  AI_TITLE_AND_CATEGORY_CREDIT_COST,
} from '../../utils/aiCredits/costs';
import { useProductStore } from '../../stores/productStore';
import { getCategoryyahooByValue, CATEGORIES_LOAD_PROMISE } from '../../utils/categories';
import { CategorySelectorAuto } from '../settings/CategorySelectorAuto';
import { hideGlobalLoading, showGlobalLoading } from '../../hooks/useLoading';

interface ProductCompactViewProps {
  product: Product;
  onExpand: () => void;
  onDelete: () => void;
  isSelected?: boolean;
  onSelect?: (selected: boolean, event?: React.MouseEvent) => void;
  onUpdate?: (product: Product) => Promise<void>;
  isGeneratingBulk?: boolean;
  onOpenPreview?: (imageIndex: number) => void;
}

export function ProductCompactView({
  product,
  onExpand,
  onDelete,
  isSelected = false,
  onSelect,
  onUpdate,
  isGeneratingBulk = false,
  onOpenPreview
}: ProductCompactViewProps) {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingTitleAndCategory, setIsGeneratingTitleAndCategory] = useState(false);
  const [showAutoExtensionDropdown, setShowAutoExtensionDropdown] = useState(false);
  const [autoExtension, setAutoExtension] = useState(product.autoExtension);
  const [autoExtensionRate, setAutoExtensionRate] = useState(product.autoExtensionRate);
  const [autoExtensionCount, setAutoExtensionCount] = useState(product.autoExtensionCount);
  const [error, setError] = useState<string | null>(null);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  const [isCategoriesLoaded, setIsCategoriesLoaded] = useState(false);
  const categorySelectorRef = useRef<HTMLDivElement>(null);
  const { templates } = useDescriptionTemplateStore();
  const { planCredits, chargedCredits  } = useAiCredits();
  const { loadCredits } = useAiCreditsStore();
  const [isDragging, setIsDragging] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const templateSelectorRef = useRef<HTMLDivElement>(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const autoExtensionRef = useRef<HTMLDivElement>(null);

  const displayTitle = createDisplayTitle(product);
  const itemNumbers = getItemNumberDisplay(product);
  const characterCount = calculateCharacterCount(product);

  

  // Get first 3 levels of category path
  const getThreeLayerCategoryPath = (categoryPath: string | undefined): string => {
    if (!categoryPath) return '';
    const pathParts = categoryPath.split(' > ');
    return pathParts.slice(0, 3).join(' > ');
  };

  // Wait for categories to load
  useEffect(() => {
    CATEGORIES_LOAD_PROMISE.then(() => {
      setIsCategoriesLoaded(true);
    }).catch((error) => {
      console.error('Failed to load categories:', error);
      setIsCategoriesLoaded(true); // Set to true anyway to avoid infinite loading
    });
  }, []);

  // Get shipping methods text
  const getShippingMethods = (product: Product): string => {
    const methods = [];
    if (product.nekoposu) methods.push('ネコポス');
    if (product.nekoCompact) methods.push('宅急便コンパクト');
    if (product.nekoEazy) methods.push('宅急便');
    if (product.yuPacketMini) methods.push('ゆうパケットポストmini');
    if (product.yuPacket) methods.push('ゆうパケット');
    if (product.yuPacketPlus) methods.push('ゆうパケットプラス');
    if (product.yuPack) methods.push('ゆうパック');
    return methods.join('、') || 'なし';
  };

  const shippingMethods = getShippingMethods(product);

  // Get template name if a template is being used
  const templateName = product.descriptionTemplateId ? 
    templates.find(t => t.id === product.descriptionTemplateId)?.name : 
    null;

  // Get Yahoo category name
  const yahooCategory = useMemo(() => {
    if (!isCategoriesLoaded) {
      return null;
    }
    return product.categoryYahoo ? 
      getCategoryyahooByValue(product.categoryYahoo) : 
      null;
  }, [product.categoryYahoo, isCategoriesLoaded]);

  const handleTemplateChange = (templateId: string | null) => {
    if (onUpdate) {
      const updatedProduct = {
        ...product,
        descriptionTemplateId: templateId,
        description: templateId === null ? '' : product.description
      };
      onUpdate(updatedProduct);
      setShowTemplateSelector(false);
    }
  };

  // Close category selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categorySelectorRef.current && !categorySelectorRef.current.contains(event.target as Node)) {
        setShowCategorySelector(false);
      }
    };

    if (showCategorySelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategorySelector]);

  // Close template selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (templateSelectorRef.current && !templateSelectorRef.current.contains(event.target as Node)) {
        setShowTemplateSelector(false);
      }
    };

    if (showTemplateSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTemplateSelector]);

  // 自動再出品設定ドロップダウンの外部クリックハンドラ
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autoExtensionRef.current && !autoExtensionRef.current.contains(event.target as Node)) {
        setShowAutoExtensionDropdown(false);
      }
    };

    if (showAutoExtensionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAutoExtensionDropdown]);

  // 自動再出品設定を保存する関数
  const handleAutoExtensionSave = () => {
    if (onUpdate) {
      const updatedProduct = {
        ...product,
        autoExtension,
        autoExtensionRate,
        autoExtensionCount
      };
      onUpdate(updatedProduct);
      setShowAutoExtensionDropdown(false);
    }
  };

  const handleMemoChange = (value: string) => {
    if (onUpdate) {
      const updatedProduct = {
        ...product,
        memo: value
      };
      onUpdate(updatedProduct);
    }
  };

  const handleTitleChange = (value: string) => {
    if (onUpdate) {
      const updatedProduct = {
        ...product,
        title: value
      };
      onUpdate(updatedProduct);
    }
  };

  const handlePriceUpdate = (startPrice: number, buyItNowPrice?: number) => {
    if (onUpdate) {
      const updatedProduct = {
        ...product,
        startPrice,
        buyItNowPrice
      };
      onUpdate(updatedProduct);
    }
  };

  const handleGenerateTitle = async () => {
    if (!product.images.length || !onUpdate) return;

    if (planCredits + chargedCredits < AI_TITLE_GENERATION_CREDIT_COST) {
      setShowCreditsModal(true);
      return;
    }

    setIsGenerating(true);
    setError(null);
    
    try {
      const user = useAuthStore.getState().user;
      if (!user?.id) {
        throw new Error('ユーザー認証が必要です');
      }

      // Generate title (credit consumption is handled in Edge Function)
      const generatedTitle = await generateTitleFromImages(product.images, user.id, product.category);
      
      const updatedProduct = {
        ...product,
        title: generatedTitle
      };
      onUpdate(updatedProduct);
    } catch (error) {
      console.error('AI title generation failed:', error);
      
      // Check if the error is due to insufficient credits
      if (error instanceof Error && error.message.includes('AIクレジットが不足')) {
        setShowCreditsModal(true);
      }
      
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('AIクレジットが不足しているか、タイトルの生成に失敗しました');
      }
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsGenerating(false);

      try {
        await loadCredits();
      } catch (error) {
        console.error('Failed to reload AI credits:', error);
      }
    }
  };

  const handleGenerateTitleAndCategory = async () => {
    if (!product.images.length || !onUpdate) return;

    if (planCredits + chargedCredits < AI_TITLE_AND_CATEGORY_CREDIT_COST) {
      setShowCreditsModal(true);
      return;
    }

    setIsGeneratingTitleAndCategory(true);
    setError(null);

    try {
      const user = useAuthStore.getState().user;
      if (!user?.id) {
        throw new Error('ユーザー認証が必要です');
      }

      const result = await generateTitleAndCategoryFromImages(
        product.images,
        user.id,
        product.category
      );

      const updatedProduct = {
        ...product,
        title: result.title,
        ...(result.categoryResolved
          ? { category: result.category, categoryYahoo: result.categoryYahoo }
          : {})
      };
      await onUpdate(updatedProduct);

      if (!result.categoryResolved) {
        setError('タイトルを生成しました。カテゴリは自動判定できなかったため、手動で選択してください。');
        setTimeout(() => setError(null), 5000);
      }
    } catch (error) {
      console.error('AI title and category generation failed:', error);

      if (error instanceof Error && error.message.includes('AIクレジットが不足')) {
        setShowCreditsModal(true);
      }

      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('タイトルとカテゴリの生成に失敗しました');
      }
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsGeneratingTitleAndCategory(false);

      try {
        await loadCredits();
      } catch (error) {
        console.error('Failed to reload AI credits:', error);
      }
    }
  };

  const isAiBusy = isGenerating || isGeneratingTitleAndCategory || isGeneratingBulk;
  const totalAvailableCredits = planCredits + chargedCredits;
  const hasCreditsForTitle = totalAvailableCredits >= AI_TITLE_GENERATION_CREDIT_COST;
  const hasCreditsForTitleAndCategory = totalAvailableCredits >= AI_TITLE_AND_CATEGORY_CREDIT_COST;

  const handleImageClick = (index: number) => {
    if (onOpenPreview) {
      onOpenPreview(index);
    } else {
      setPreviewImageIndex(index);
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(!isSelected, e);
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    
    // 選択された商品がある場合は、選択された全商品を移動対象とする
    const selectedProducts = useProductStore.getState().products.filter(p => p.isSelected);
    const isProductSelected = selectedProducts.some(p => p.id === product.id);
    
    if (isProductSelected && selectedProducts.length > 1) {
      // 複数選択時は選択された全商品のIDを送信
      const selectedIds = selectedProducts.map(p => p.id);
      e.dataTransfer.setData('text/plain', `products:${selectedIds.join(',')}`);
    } else {
      // 単一商品の移動
      e.dataTransfer.setData('text/plain', `product:${product.id}`);
    }
    
    // Create a custom drag preview
    const dragPreview = document.createElement('div');
    dragPreview.className = 'bg-white p-2 rounded shadow-md border border-blue-300 max-w-xs';
    
    if (isProductSelected && selectedProducts.length > 1) {
      dragPreview.textContent = `${selectedProducts.length}個の商品を移動`;
    } else {
      dragPreview.textContent = displayTitle || `商品 ${product.serialNumber}`;
    }
    
    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 20, 20);
    
    // Remove the preview element after it's been used
    setTimeout(() => {
      document.body.removeChild(dragPreview);
    }, 0);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (onUpdate) {
      const updatedProduct = {
        ...product,
        condition: e.target.value
      };
      onUpdate(updatedProduct);
    }
  };

  const handleCategoryChange = (selected: any) => {
   
    if (onUpdate && selected) {
      const updatedProduct = {
        ...product,
        category:selected.path,
        categoryYahoo: selected.value
      };

      onUpdate(updatedProduct);
      setShowCategorySelector(false);
    }
  };

  // Only show wave animation if the product is selected and bulk generation is active
  const showWaveAnimation = isSelected && isGeneratingBulk;

  if (!onUpdate) {
    return null;
  }

  return (
    <>
      <DragDropImageUpload product={product} onUpdate={onUpdate}>
        <div 
          className={`bg-white rounded-lg shadow-sm border border-gray-200 hover:border-gray-300 transition-colors cursor-move ${
            isDragging ? 'opacity-50 transform scale-95' : ''
          }`}
          draggable
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="p-5">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Image Section */}
              <div className="flex items-start gap-4 lg:w-48">
                {onSelect && (
                  <div className="pt-0.5\" onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-6 h-6"
                    />
                  </div>
                )}

                <div className="flex-1 lg:w-full">
                  <div className="relative">
                    {(product.thumbnailImages && product.thumbnailImages.length > 0) || product.images.length > 0 ? (
                      <div className="aspect-square w-full mb-0">
                        <img
                          src={product.thumbnailImages && product.thumbnailImages.length > 0 ? product.thumbnailImages[0] : product.images[0]}
                          alt="商品画像"
                          className="w-full h-full object-cover rounded-md cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick(0);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="aspect-square w-full bg-gray-50 border-2 border-dashed border-gray-200 rounded-md flex flex-col items-center justify-center text-gray-400 hover:bg-gray-100 hover:border-gray-300 transition-colors">
                        <Upload className="w-10 h-10 mb-2" />
                        <span className="text-base">写真を追加</span>
                        <span className="text-sm mt-1">ドラッグ＆ドロップ可能</span>
                      </div>
                    )}
                  </div>
                  
                 
                  
                  {/* 新しいカテゴリ表示 - サムネイル画像の下に配置 */}
                  <div className="mt-3 w-full" ref={categorySelectorRef}>
                    <button
                      onClick={() => setShowCategorySelector(!showCategorySelector)}
                      className={`w-full py-3 px-4 rounded-lg text-sm font-semibold text-center transition-all duration-200 flex items-center justify-center gap-2 ${
                        !isCategoriesLoaded
                          ? 'bg-gray-100 text-gray-500 cursor-wait'
                          : yahooCategory 
                            ? 'bg-yellow-500 text-white hover:bg-yellow-700 shadow-md hover:shadow-lg' 
                            : 'bg-blue-500 text-white hover:bg-blue-600 shadow-md hover:shadow-lg' 
                      }`}
                      disabled={!isCategoriesLoaded}
                    >
                      <Folder className="w-5 h-5" />
                      <span className="font-medium">
                        {!isCategoriesLoaded 
                          ? '読み込み中...' 
                          : yahooCategory 
                            ? getThreeLayerCategoryPath(yahooCategory.path || yahooCategory.label) || yahooCategory.label
                            : 'カテゴリを選択'}
                      </span>
                    </button>
                    
                    {showCategorySelector && isCategoriesLoaded && (
                      <div className="absolute z-50 mt-2 w-full bg-white shadow-xl rounded-lg border border-gray-200">
                        <div className="p-3">
                          <CategorySelectorAuto 
                            valueYahoo={product.categoryYahoo}
                            onChangeYahoo={handleCategoryChange}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="flex-1 min-w-0 flex flex-col">
                {/* Top row with tags */}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {/* 商品の状態 */}
                  <div className="inline-block">
                    <select
                      value={product.condition}
                      onChange={handleConditionChange}
                      className="px-2 py-1 rounded-md text-xs font-medium border-0 focus:ring-2 focus:ring-blue-500 focus:outline-none h-7"
                      style={{
                        backgroundColor: 
                          product.condition === 'new' ? 'rgb(239 246 255)' : // blue-50
                          product.condition === 'like-new' ? 'rgb(240 253 244)' : // green-50
                          product.condition === 'good' ? 'rgb(240 253 244)' : // green-50
                          product.condition === 'fair' ? 'rgb(254 252 232)' : // yellow-50
                          product.condition === 'poor' ? 'rgb(255 247 237)' : // orange-50
                          product.condition === 'bad' ? 'rgb(254 242 242)' : // red-50
                          'rgb(249 250 251)', // gray-100
                        color: 
                          product.condition === 'new' ? 'rgb(37 99 235)' : // blue-600
                          product.condition === 'like-new' ? 'rgb(22 163 74)' : // green-600
                          product.condition === 'good' ? 'rgb(22 163 74)' : // green-600
                          product.condition === 'fair' ? 'rgb(202 138 4)' : // yellow-600
                          product.condition === 'poor' ? 'rgb(234 88 12)' : // orange-600
                          product.condition === 'bad' ? 'rgb(220 38 38)' : // red-600
                          'rgb(75 85 99)' // gray-600
                      }}
                    >
                      <option value="">状態を選択</option>
                      <option value="new">未使用</option>
                      <option value="like-new">未使用に近い</option>
                      <option value="good">目立った傷や汚れなし</option>
                      <option value="fair">やや傷や汚れあり</option>
                      <option value="poor">傷や汚れあり</option>
                      <option value="bad">全体的に状態が悪い</option>
                    </select>
                  </div>

                  {/* 送料設定 */}
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                    product.shippingResponsibility === 'seller'
                      ? 'bg-red-50 text-red-600'
                      : product.shippingCOD ? 'bg-yellow-50 text-yellow-600'
                      : product.shippingLater ? 'bg-purple-50 text-purple-600'
                      : 'bg-red-50 text-red-600'
                  }`}>
                    <Truck className="w-4 h-4" />
                    {product.shippingResponsibility === 'seller'
                      ? '送料込み'
                      : product.shippingCOD
                        ? '着払い'
                        : product.shippingLater
                          ? '落札後に送料を連絡'
                          : '落札者負担'}
                  </span>


                  {/* 自動再出品設定 */}
                  <div className="inline-flex relative" ref={autoExtensionRef}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAutoExtensionDropdown(!showAutoExtensionDropdown);
                      }}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                        product.autoExtension 
                          ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' 
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                        <path d="M3 3v5h5"></path>
                        <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path>
                        <path d="M16 16h5v5"></path>
                      </svg>
                      <span>{product.autoExtension ? `自動再出品 ${product.autoExtensionRate}%引き` : '自動再出品'}</span>
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </button>
                    
                    {showAutoExtensionDropdown && (
                      <div className="absolute z-50 mt-1 w-64 bg-white shadow-lg rounded-md border border-gray-200"> 
                        <div className="p-3">
                          <div className="mb-3">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={autoExtension}
                                onChange={(e) => setAutoExtension(e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">自動再出品を有効にする</span>
                            </label>
                          </div>
                          
                          {autoExtension && (
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">値下げ率</label>
                                <select
                                  value={autoExtensionRate}
                                  onChange={(e) => setAutoExtensionRate(Number(e.target.value))}
                                  className="w-full text-sm rounded-md border-gray-300 bg-gray-50"
                                >
                                  <option value="0">値下げしない</option>
                                  <option value="5">5%</option>
                                  <option value="10">10%</option>
                                  <option value="15">15%</option>
                                  <option value="20">20%</option>
                                  <option value="25">25%</option>
                                  <option value="30">30%</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">再出品回数</label>
                                <select
                                  value={autoExtensionCount}
                                  onChange={(e) => setAutoExtensionCount(Number(e.target.value))}
                                  className="w-full text-sm rounded-md border-gray-300 bg-gray-50"
                                >
                                  {[1, 2, 3].map(count => (
                                    <option key={count} value={count}>{count}回</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}
                          
                          <div className="mt-3 flex justify-end">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAutoExtensionSave();
                              }}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                            >
                              保存
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 注目のオークション */}
                  {product.isFeatured && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-600">
                      <Star className="w-4 h-4" />
                      注目のオークション
                    </span>
                  )}

                  {/* おすすめコレクション */}
                  {product.recommendedCollection && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-600">
                      <Sparkles className="w-4 h-4" />
                      おすすめコレクション
                    </span>
                  )}

                  {/* 説明テンプレート */}
                  {templateName ? (
                    <div className="relative inline-flex" ref={templateSelectorRef}>
                      <button 
                        onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                      >
                        <FileText className="w-4 h-4" />
                        {templateName}
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </button>
                      
                      {showTemplateSelector && (
                        <div className="absolute z-50 mt-1 w-64 bg-white shadow-lg rounded-md border border-gray-200">
                          <div className="p-2">
                            <DescriptionTemplateSelector
                              selectedTemplateId={product.descriptionTemplateId}
                              onChange={handleTemplateChange}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="relative inline-flex" ref={templateSelectorRef}>
                      <button 
                        onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-50 text-gray-600 hover:bg-gray-100"
                      >
                        <FileText className="w-4 h-4" />
                        説明テンプレート
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </button>
                      
                      {showTemplateSelector && (
                        <div className="absolute z-50 mt-1 w-64 bg-white shadow-lg rounded-md border border-gray-200">
                          <div className="p-2">
                            <DescriptionTemplateSelector
                              selectedTemplateId={product.descriptionTemplateId}
                              onChange={handleTemplateChange}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Title Section */}
                <div className="relative mb-4">
                  <EditableTitle
                    title={isTitleEditing ? product.title : displayTitle}
                    isGenerating={isGenerating || isGeneratingBulk}
                    product={product}
                    onTitleChange={handleTitleChange}
                    onEditStateChange={setIsTitleEditing}
                    isGeneratingBulk={showWaveAnimation}
                  />
                  <div className="absolute -bottom-7 right-0 flex flex-wrap gap-2 text-sm">
                    <span className={`font-bold ${characterCount.isValid ? 'text-blue-700' : 'text-red-600'}`}>
                      {characterCount.total}/65文字
                    </span>
                    {characterCount.itemNumbers > 0 && (
                      <span className="font-bold text-blue-700">
                        管理番号: {characterCount.itemNumbers}文字
                      </span>
                    )}
                    {characterCount.memo > 0 && (
                      <span className="font-bold text-blue-700">
                        メモ: {characterCount.memo}文字
                      </span>
                    )}
                  </div>
                </div>

                {/* Price Section - Adjusted spacing */}
                <div className="mb-2">
                  <PriceDisplay
                    listingType={product.listingType}
                    startPrice={product.startPrice}
                    buyItNowPrice={product.buyItNowPrice}
                    onUpdate={handlePriceUpdate}
                  />
                </div>

                {/* Shipping Methods */}
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                  <Package className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{shippingMethods || '配送方法なし'}</span>
                </div>

                {/* Bottom Section */}
                <div className="pt-3 border-t">
                  <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <MemoInput
                        value={product.memo}
                        onChange={handleMemoChange}
                        onClick={(e) => e.stopPropagation()}
                        product={product}
                      />
                    </div>
                    
                    <div className="flex w-full flex-col gap-2 lg:w-auto lg:flex-row lg:items-center lg:gap-2 shrink-0">
                      <button
                        onClick={onExpand}
                        className="flex w-full lg:w-auto items-center justify-center gap-2 px-3 py-2 bg-white text-gray-700 rounded-md shadow-sm border border-gray-200 text-sm font-medium transition-colors hover:bg-gray-50 whitespace-nowrap"
                      >
                        <Edit className="w-4 h-4 shrink-0" />
                        <span>編集</span>
                      </button>

                      {product.images.length > 0 && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerateTitle();
                            }}
                            disabled={isAiBusy || !hasCreditsForTitle}
                            className={`btn-ai-compact flex w-full lg:w-auto items-center justify-center gap-2 px-3 py-2 text-sm whitespace-nowrap ${
                              showWaveAnimation ? 'animate-pulse' : ''
                            }`}
                            title="AIでタイトルを生成"
                          >
                            <Wand2 className={`w-4 h-4 shrink-0 ${isGenerating ? 'animate-spin' : ''}`} />
                            <span>タイトル生成</span>
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerateTitleAndCategory();
                            }}
                            disabled={isAiBusy || !hasCreditsForTitleAndCategory}
                            className={`flex w-full lg:w-auto items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-[#2563EB] via-[#0EA5E9] to-[#22D3EE] hover:from-blue-700 hover:via-sky-600 hover:to-cyan-500 whitespace-nowrap ${
                              isGeneratingTitleAndCategory ? 'animate-pulse' : ''
                            }`}
                            title="AIでタイトルとカテゴリを生成"
                          >
                            <Sparkles className={`w-4 h-4 shrink-0 ${isGeneratingTitleAndCategory ? 'animate-spin' : ''}`} />
                            <span>タイトルとカテゴリ生成</span>
                          </button>
                        </>
                      )}

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteConfirmation(true);
                        }}
                        className="flex w-full lg:w-auto items-center justify-center gap-2 px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md border border-red-100 lg:border-transparent lg:p-2 lg:rounded-full transition-colors whitespace-nowrap"
                      >
                        <Trash2 className="w-4 h-4 shrink-0" />
                        <span className="lg:hidden">削除</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showDeleteConfirmation && (
          <DeleteConfirmationModal
            message={`商品「${displayTitle}」を削除してもよろしいですか？この操作は元に戻せません。`}
            onConfirm={() => {
              showGlobalLoading();
              try {
                onDelete();
              } catch (error) {
                console.error('Failed to delete product:', error);
                alert('商品削除に失敗しました。もう一度お試しください。');
              }
              hideGlobalLoading();
              setShowDeleteConfirmation(false);
            }}
            onCancel={() => setShowDeleteConfirmation(false)}
          />
        )}

        {previewImageIndex !== null && (
          <ImagePopup
            images={product.images}
            currentIndex={previewImageIndex}
            title={displayTitle}
            onClose={() => setPreviewImageIndex(null)}
            onPrevious={() => setPreviewImageIndex(prev => prev?  Math.max(0, prev - 1): 0)}
            onNext={() => setPreviewImageIndex(prev => prev? Math.min(product.images.length - 1, prev + 1) :product.images.length - 1 )}
          />
        )}
      </DragDropImageUpload>

      {showCreditsModal && (
        <InsufficientCreditsModal onClose={() => setShowCreditsModal(false)} />
      )}
    </>
  );
}